import React, { useContext, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  cancelSubmit,
  deleteRecord,
  getLeaveTypeData,
  getRecords,
  handleInputChange,
  handleOtherInputChange,
  handleSubmit,
} from '../../CommonFunction'
import { changeTextCamal, checkRole, clearInputError, getDate } from '../../../_metronic/helpers'
import {
  InputErrorType,
  RoleContextValue,
  SelectValueType,
  SubmitAPIStatusType,
} from '../../types/OtherTypes'
import { APIStatusData, SubmitAPIStatusData } from '../../data/OtherDefaultData'
import { EDIT_ROW_COLORS } from '../../../utils/contants'
import ActionColumn from '../../components/ActionColumn'
import {
  getLeavePolicyByID,
  getLeavePolicyTableHeaderData,
  syncLeavePolicyUsers,
  validateForm,
} from './LeavePolicyHelperFunction'
import CustomTextInput from '../../components/CustomInput/CustomTextInput'
import CustomNumberInput from '../../components/CustomInput/CustomNumberInput'
import SubmitCancleButton from '../../components/SubmitCancelButton'
import CustomTable from '../../components/CustomTable'
import { Button as MantineButton, SelectItem, Alert, MultiSelect } from '@mantine/core'
import Button from '../../components/Button/Button'

import CustomRadioInput from '../../components/CustomInput/CustomRadioInput'
import { Modal } from 'react-bootstrap'
import { LeavePolicyInput, LeavePolicyOutput } from '../../types/SettingModuleTypes'
import { LeavePolicyInitialInput } from '../../data/SettingModulesDefaultData'
import { addLeavePolicies, updateLeavePoliciesById } from '../../../api/ConfigurationAPI'
import API from '../../../api/APIUrl'
import Select2 from '../../components/select2'
import roleContext from '../../context/roleContext'
import { LeavePolicyUIService, CASUAL_LEAVE_PRORATE_TABLE, EARNED_LEAVE_PRORATE_TABLE, LWP_EL_REDUCTION_TABLE } from '../../services/LeavePolicyUIService'

const LeavePolicy = () => {
  let roleState: RoleContextValue = useContext(roleContext)
  const [editId, setEditId] = useState<string | null>(null)
  const [submitAPIStatus, setSubmitAPIStatus] = useState<SubmitAPIStatusType>(SubmitAPIStatusData)
  const [showForm, setShowForm] = useState<boolean>(false)
  const [showPolicyTable, setShowPolicyTable] = useState<boolean>(false)
  const { leavePolicyTableHeaderData } = getLeavePolicyTableHeaderData(
    !checkRole(['leave_policies_edit'], roleState.features)
  )
  const [inputError, setInputError] = useState<InputErrorType>({})
  const [leaveType, setLeaveType] = useState<SelectItem[]>([])
  const [leaveTypeLoading, setLeaveTypeLoading] = useState<boolean>(false)
  const [leavePolicy, setLeavePolicy] = useState<LeavePolicyOutput>({ ...APIStatusData, data: [] })
  const [formField, setFormField] = useState<LeavePolicyInput>(LeavePolicyInitialInput)
  const [selectedLeaveTypeName, setSelectedLeaveTypeName] = useState<string>('')
  const [selectedApplicableLeaves, setSelectedApplicableLeaves] = useState<string[]>([])

  // Get UI configuration for selected leave type
  const uiConfig = useMemo(() => {
    return LeavePolicyUIService.getUIConfigForLeaveType(selectedLeaveTypeName)
  }, [selectedLeaveTypeName])

  // Helpers for CL Pro‑rata dynamic table
  const roundToNearestHalf = (value: number) => {
    return Math.round(value * 2) / 2
  }

  const clProRataRows = useMemo(() => {
    // Default baseline total for CL is 8; scale from this if user edits
    const baseline = 8
    const total = Number(formField.totalPerYear) || baseline
    const scale = total / baseline
    return CASUAL_LEAVE_PRORATE_TABLE.map((row) => {
      const base = row.entitlement
      const ent = base === 0 ? 0 : roundToNearestHalf(base * scale)
      return {
        month: row.month,
        entitlement: ent,
      }
    })
  }, [formField.totalPerYear])

  // Helpers for EL Pro‑rata dynamic table
  const elProRataRows = useMemo(() => {
    const baseline = 14
    const total = Number(formField.totalPerYear) || baseline
    const scale = total / baseline
    return EARNED_LEAVE_PRORATE_TABLE.map((row) => {
      const base = row.entitlement
      const ent = base === 0 ? 0 : roundToNearestHalf(base * scale)
      return {
        month: row.month,
        entitlement: ent,
      }
    })
  }, [formField.totalPerYear])

  // Wrapper validation function that includes leave type name
  const validateFormWithLeaveType = (formData: LeavePolicyInput) => {
    // Ensure leave types are loaded before validation
    if (leaveTypeLoading) {
      return { error: { leaveTypeId: 'Please wait for leave types to load' }, cnt: 1 }
    }

    // Validate that the selected leave type still exists
    // Relaxed check: Pre-existing policies might refer to types not currently in the dropdown list (e.g. inactive)
    // if (formData.leaveTypeId && !leaveType.find(lt => lt.value === formData.leaveTypeId)) {
    //   return { error: { leaveTypeId: 'Selected leave type is no longer available' }, cnt: 1 }
    // }

    return validateForm(formData, selectedLeaveTypeName)
  }

  // Add custom styles
  const customStyles = `
    .leave-policy-modal .modal-dialog {
      max-width: 65vw !important;
      width: 65vw !important;
    }
    
    @media (max-width: 1200px) {
      .leave-policy-modal .modal-dialog {
        max-width: 75vw !important;
        width: 75vw !important;
      }
    }
    
    @media (max-width: 768px) {
      .leave-policy-modal .modal-dialog {
        max-width: 95vw !important;
        width: 95vw !important;
      }
    }
    
    .leave-policy-modal .modal-content {
      border-radius: 12px;
      border: none;
      box-shadow: 0 20px 40px rgba(0,0,0,0.1);
      max-height: 85vh;
      display: flex;
      flex-direction: column;
    }
    
    .leave-policy-modal .modal-body {
      flex: 1;
      overflow-y: auto;
      max-height: calc(85vh - 120px); /* Subtract header and footer height */
    }
    
    .leave-policy-modal .modal-footer {
      flex-shrink: 0;
      position: sticky;
      bottom: 0;
      background: #f8f9fa;
      z-index: 1000;
      padding: 1.25rem 1.5rem !important;
      border-top: 1px solid #dee2e6;
    }
    
    .bg-gradient-primary {
      background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
    }
    
    .bg-gradient-success {
      background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%);
    }
    
    .bg-gradient-danger {
      background: linear-gradient(135deg, #dc3545 0%, #bd2130 100%);
    }
    
    .card {
      border-radius: 8px;
      transition: all 0.3s ease;
    }
    
    .table-hover tbody tr:hover {
      background-color: rgba(0,123,255,0.05);
    }
    
    .badge {
      font-size: 0.75rem;
      font-weight: 500;
    }
    
    .modal-header {
      border-radius: 12px 12px 0 0;
    }
  `
  const getTableBody = () => {
    return leavePolicy?.data.map((obj) => {
      return (
        <tr key={obj?.id} className={`${editId === obj?.id ? `${EDIT_ROW_COLORS} ` : ''}`}>
          <td>{changeTextCamal(obj?.leaveType?.name ?? '-')}</td>
          <td className='text-gray-800'>{obj?.totalPerYear ?? (obj as any)?.total_per_year ?? '0'}</td>
          <td className='text-gray-800'>{obj?.maxDaysPerRequest ?? (obj as any)?.max_days_per_request ?? '-'}</td>
          <td className='text-gray-800'>{obj?.minAdvanceNotice ?? (obj as any)?.min_advance_notice ?? '-'}</td>
          <td className='text-gray-800 '>{obj?.allowBackdated ? 'Yes' : 'No'}</td>
          <td className='text-gray-800 '>{obj?.allowEmergency ? 'Yes' : 'No'}</td>
          <td className='text-gray-800 '>{obj?.isPaid ? 'Yes' : 'No'}</td>
          <td className='text-gray-800 '>{obj?.isProRated ? 'Yes' : 'No'}</td>
          <td className='text-gray-800 '>{obj?.allowCarry ? 'Yes' : 'No'}</td>
          <td className='text-gray-800 '>{obj?.carryLimit || '-'}</td>
          <td hidden={!checkRole(['leave_policies_edit'], roleState.features)}>
            {/* Disable edit/delete for system default policies (Short Leave, LWP) */}
            {obj?.isSystemDefault || obj?.leaveType?.isSystemDefault ? (
              <div className="d-flex align-items-center justify-content-center" title="System default policy - cannot be edited or deleted">
                <i className="ki-duotone ki-lock fs-2 text-muted">
                  <span className="path1"></span>
                  <span className="path2"></span>
                </i>
              </div>
            ) : (
              <ActionColumn
                edit={{
                  onClick: () => {
                    // Clear previous state before editing
                    setSelectedLeaveTypeName('')
                    setInputError({})
                    setSelectedApplicableLeaves([])

                    getLeavePolicyByID(
                      obj.id,
                      formField,
                      setFormField,
                      setEditId,
                      setShowForm,
                      setInputError,
                      setSelectedLeaveTypeName,
                      setSelectedApplicableLeaves
                    )
                  },
                }}
                remove={{
                  onClick: () =>
                    deleteRecord(
                      `${API.LEAVEPOLICY}/${obj.id}`,
                      setLeavePolicy,
                      setFormField,
                      setEditId,
                      setInputError,
                      LeavePolicyInitialInput,
                      setShowForm,
                      `${API.LEAVEPOLICY}`
                    ),
                }}
              />
            )}
          </td>
        </tr>
      )
    })
  }

  // Load leave types once when component mounts (not just when form opens)
  useEffect(() => {
    if (leaveType.length === 0 && !leaveTypeLoading) {
      setLeaveTypeLoading(true)
      getLeaveTypeData((data: SelectItem[]) => {
        setLeaveType(data)
        setLeaveTypeLoading(false)
      })
    }
  }, [])

  // Handle leave type change to update UI configuration
  const handleLeaveTypeChange = (selectedValue: string) => {
    // Prevent selection if leave types are still loading
    if (leaveTypeLoading) {
      toast.error('Please wait for leave types to load')
      return
    }

    const selectedLeave = leaveType.find(lt => lt.value === selectedValue)
    const leaveTypeName = selectedLeave?.label || ''

    // Additional validation: ensure the selected value exists in the loaded data
    if (!selectedLeave) {
      console.error('Selected leave type not found in loaded data:', selectedValue)
      toast.error('Invalid leave type selection')
      return
    }

    setSelectedLeaveTypeName(leaveTypeName)

    // Apply UI defaults for the selected leave type
    const updatedFormField = LeavePolicyUIService.applyUIDefaults(leaveTypeName, {
      ...formField,
      leaveTypeId: selectedValue
    })

    setFormField(updatedFormField)
    clearInputError('leaveTypeId', inputError, setInputError)
  }

  // Clear state when form is hidden
  useEffect(() => {
    if (!showForm) {
      setSelectedLeaveTypeName('')
      setInputError({})
      setSelectedApplicableLeaves([]) // Clear selected applicable leaves
    }
  }, [showForm])

  // Update form field when applicable leaves selection changes
  useEffect(() => {
    if (selectedApplicableLeaves.length > 0) {
      setFormField((prev: any) => ({
        ...prev,
        proRataApplicableLeaves: selectedApplicableLeaves.join(', ')
      }))
    } else if (formField.proRataApplicableLeaves) {
      // Only clear if there was a value before
      setFormField((prev: any) => ({
        ...prev,
        proRataApplicableLeaves: ''
      }))
    }
  }, [selectedApplicableLeaves])

  useEffect(() => {
    let mounted = true
    if (mounted) {
      getRecords(`${API.LEAVEPOLICY}`, setLeavePolicy)
    }
    return () => {
      mounted = false
    }
  }, [])
  return (
    <>
      <style>{customStyles}</style>
      <CustomTable
        data={leavePolicy}
        setData={setLeavePolicy}
        url={`${API.LEAVEPOLICY}`}
        tableBody={getTableBody}
        tableHeadData={leavePolicyTableHeaderData}
        tableLabel='Leave Policies'
        isSortingRequired={false}
        newRecordButton={
          <>
            <Button
              variant='primary'
              size='sm'
              leftIcon={<i className='fas fa-plus'></i>}
              onClick={() => {
                // Clear all state when adding new policy
                setSelectedLeaveTypeName('')
                setInputError({})
                setFormField(LeavePolicyInitialInput)
                setShowForm(true)
              }}
              style={{ display: showForm || !checkRole(['leave_policies_edit'], roleState.features) ? 'none' : 'inline-flex' }}
            >
              Add Leave Policies
            </Button>
          </>
        }
      />
      <Modal
        show={showForm}
        animation={false}
        backdrop='static'
        centered
        className='leave-policy-modal'
      >
        <div className='modal-header bg-primary text-white'>
          <h2 className='fw-bolder mb-0 text-white d-flex align-items-center'>
            <i className='fas fa-calendar-check me-2'></i>
            {editId !== null ? 'Update' : 'Add'} Leave Policies
          </h2>
          <div
            className='btn btn-icon btn-sm btn-active-icon-light'
            data-kt-users-modal-action='close'
            style={{ cursor: 'pointer' }}
            onClick={() => {
              // Clear all state when canceling
              setSelectedLeaveTypeName('')
              setInputError({})
              cancelSubmit(
                setFormField,
                setEditId,
                setInputError,
                LeavePolicyInitialInput,
                setShowForm
              )
            }}
          >
            <i className='fa-solid fa-xmark fs-2 text-white' />
          </div>
        </div>
        <div className='modal-body p-4'>
          {selectedLeaveTypeName && (
            <Alert color="blue" variant="filled" className="mb-4">
              <div className='d-flex align-items-center text-white'>
                <i className='fas fa-info-circle me-3 fs-4'></i>
                <div>
                  <strong className='text-white'>{selectedLeaveTypeName} Policy:</strong>
                  <div className='text-white opacity-90 mt-1'>
                    {LeavePolicyUIService.getLeavePolicySummary(selectedLeaveTypeName)}
                  </div>
                </div>
              </div>
            </Alert>
          )}

          <div className='row g-3'>
            {/* Form Fields Section - Dynamic width based on table visibility */}
            <div className={`${(selectedLeaveTypeName === 'Casual Leave' || selectedLeaveTypeName === 'Earned Leave') ? 'col-lg-8' : 'col-12'} col-md-12`}>
              <div className='card border-0 shadow-sm'>
                <div className='card-header bg-light border-0'>
                  <h5 className='card-title mb-0 text-dark'>
                    <i className='fas fa-edit me-2 text-primary'></i>
                    Leave Policy Configuration
                  </h5>
                </div>
                <div className='card-body p-4'>
                  <div className='row g-3'>
                    <div className='col-md-6'>
                      <Select2
                        name='leaveTypeId'
                        data={leaveType}
                        size='md'
                        label='Leave Type'
                        withAsterisk={true}
                        value={formField?.leaveTypeId}
                        onChange={handleLeaveTypeChange}
                        error={inputError?.leaveTypeId}
                        disabled={leaveTypeLoading}
                        placeholder={leaveTypeLoading ? 'Loading leave types...' : 'Select a leave type'}
                      />
                    </div>

                    {uiConfig.totalPerYear.visible && (
                      <div className='col-md-6'>
                        <CustomNumberInput
                          label='Total Leave Per Year'
                          name='totalPerYear'
                          value={formField.totalPerYear}
                          onChange={(event) => {
                            handleInputChange(event, setFormField, inputError, setInputError)
                          }}
                          maxLength={4}
                          disabled={uiConfig.totalPerYear.disabled}
                          error={inputError.totalPerYear}
                          withAsterisk={true}
                        />
                        {uiConfig.totalPerYear.helpText && (
                          <small className="text-muted">{uiConfig.totalPerYear.helpText}</small>
                        )}
                      </div>
                    )}

                    {uiConfig.maxDaysPerRequest.visible && (
                      <div className='col-md-6'>
                        <CustomNumberInput
                          label='Max Days Per Request'
                          name='maxDaysPerRequest'
                          value={formField.maxDaysPerRequest}
                          onChange={(event) => {
                            handleInputChange(event, setFormField, inputError, setInputError)
                          }}
                          maxLength={4}
                          disabled={uiConfig.maxDaysPerRequest.disabled}
                          error={inputError.maxDaysPerRequest}
                          withAsterisk={true}
                        />
                        {uiConfig.maxDaysPerRequest.helpText && (
                          <small className="text-muted">{uiConfig.maxDaysPerRequest.helpText}</small>
                        )}
                      </div>
                    )}

                    {uiConfig.minAdvanceNotice.visible && (
                      <div className='col-md-6'>
                        <CustomNumberInput
                          label='Min Advance Notice (Hours)'
                          name='minAdvanceNotice'
                          value={formField.minAdvanceNotice}
                          onChange={(event) => {
                            handleInputChange(event, setFormField, inputError, setInputError)
                          }}
                          maxLength={4}
                          disabled={uiConfig.minAdvanceNotice.disabled}
                          error={inputError.minAdvanceNotice}
                          withAsterisk={uiConfig.minAdvanceNotice.required}
                        />
                        {uiConfig.minAdvanceNotice.helpText && (
                          <small className="text-muted">{uiConfig.minAdvanceNotice.helpText}</small>
                        )}
                      </div>
                    )}

                    {uiConfig.allowEmergency.visible && (
                      <div className='col-md-6'>
                        <CustomRadioInput
                          name='allowEmergency'
                          label='Allow Emergency'
                          data={[
                            { label: 'Yes', value: 'true' },
                            { label: 'No', value: 'false' },
                          ]}
                          value={formField?.allowEmergency}
                          onChange={(e: any) => {
                            if (!uiConfig.allowEmergency.disabled) {
                              handleOtherInputChange(e, 'allowEmergency', setFormField, inputError, setInputError)
                            }
                          }}
                          children={undefined}
                          error={inputError?.allowEmergency}
                          withAsterisk={uiConfig.allowEmergency.required}
                        />
                        {uiConfig.allowEmergency.helpText && (
                          <small className="text-muted">{uiConfig.allowEmergency.helpText}</small>
                        )}
                      </div>
                    )}

                    {uiConfig.requiresApproval.visible && (
                      <div className='col-md-6'>
                        <CustomRadioInput
                          name='requiresApproval'
                          label='Requires Approval'
                          data={[
                            { label: 'Yes', value: 'true' },
                            { label: 'No', value: 'false' },
                          ]}
                          value={formField?.requiresApproval}
                          onChange={(e: any) => {
                            if (!uiConfig.requiresApproval.disabled) {
                              handleOtherInputChange(e, 'requiresApproval', setFormField, inputError, setInputError)
                            }
                          }}
                          children={undefined}
                          error={inputError?.requiresApproval}
                          withAsterisk={uiConfig.requiresApproval.required}
                        />
                        {uiConfig.requiresApproval.helpText && (
                          <small className="text-muted">{uiConfig.requiresApproval.helpText}</small>
                        )}
                      </div>
                    )}

                    {uiConfig.isPaid.visible && (
                      <div className='col-md-6'>
                        <CustomRadioInput
                          name='isPaid'
                          label='Is Paid'
                          data={[
                            { label: 'Yes', value: 'true' },
                            { label: 'No', value: 'false' },
                          ]}
                          value={formField?.isPaid}
                          onChange={(e: any) => {
                            if (!uiConfig.isPaid.disabled) {
                              handleOtherInputChange(e, 'isPaid', setFormField, inputError, setInputError)
                            }
                          }}
                          children={undefined}
                          error={inputError?.isPaid}
                          withAsterisk={uiConfig.isPaid.required}
                        />
                        {uiConfig.isPaid.helpText && (
                          <small className="text-muted">{uiConfig.isPaid.helpText}</small>
                        )}
                      </div>
                    )}

                    {uiConfig.isProRated.visible && (
                      <div className='col-md-6'>
                        <CustomRadioInput
                          name='isProRated'
                          label='Is Pro Rated'
                          data={[
                            { label: 'Yes', value: 'true' },
                            { label: 'No', value: 'false' },
                          ]}
                          value={formField?.isProRated}
                          onChange={(e: any) => {
                            if (!uiConfig.isProRated.disabled) {
                              handleOtherInputChange(e, 'isProRated', setFormField, inputError, setInputError)
                            }
                          }}
                          children={undefined}
                          error={inputError?.isProRated}
                          withAsterisk={uiConfig.isProRated.required}
                        />
                        {uiConfig.isProRated.helpText && (
                          <small className="text-muted">{uiConfig.isProRated.helpText}</small>
                        )}
                      </div>
                    )}

                    {uiConfig.allowCarry.visible && (
                      <div className='col-md-6'>
                        <CustomRadioInput
                          name='allowCarry'
                          label='Carry Forward?'
                          data={[
                            { label: 'Yes', value: 'true' },
                            { label: 'No', value: 'false' },
                          ]}
                          value={formField?.allowCarry}
                          onChange={(e: any) => {
                            if (!uiConfig.allowCarry.disabled) {
                              clearInputError('allowCarry', inputError, setInputError)
                              clearInputError('carryLimit', inputError, setInputError)
                              setFormField((prev: any) => ({
                                ...prev,
                                allowCarry: e,
                                carryLimit: e === 'true' ? prev.carryLimit : '',
                              }))
                            }
                          }}
                          children={undefined}
                          error={inputError?.allowCarry}
                          withAsterisk={uiConfig.allowCarry.required}
                        />
                        {uiConfig.allowCarry.helpText && (
                          <small className="text-muted">{uiConfig.allowCarry.helpText}</small>
                        )}
                      </div>
                    )}

                    {uiConfig.carryLimit.visible && formField?.allowCarry === 'true' && (
                      <div className='col-md-6'>
                        <CustomNumberInput
                          name='carryLimit'
                          label='Max Carry Forward Leave'
                          value={formField?.carryLimit}
                          onChange={(e) => {
                            handleInputChange(e, setFormField, inputError, setInputError)
                          }}
                          error={inputError.carryLimit}
                          maxLength={4}
                          disabled={uiConfig.carryLimit.disabled}
                          withAsterisk={uiConfig.carryLimit.required}
                        />
                        {uiConfig.carryLimit.helpText && (
                          <small className="text-muted">{uiConfig.carryLimit.helpText}</small>
                        )}
                      </div>
                    )}
                    {/* Pro‑rata Configuration (visible only when isProRated is Yes) */}
                    {/* COMMENTED OUT - Pro-rata Configuration Section */}
                    {/* {formField?.isProRated === 'true' && (
                      <div className='col-12'>
                        <div className='card border-0 shadow-sm'>
                          <div className='card-header bg-light border-0'>
                            <h6 className='card-title mb-0 text-dark'>
                              <i className='fas fa-sliders-h me-2 text-primary'></i>
                              Pro‑rata Configuration
                            </h6>
                          </div>
                          <div className='card-body p-3'>
                            <div className='row g-3'>
                              <div className='col-md-4'>
                                <Select2
                                  name='proRataType'
                                  data={[
                                    {label: 'Monthly', value: 'Monthly'},
                                    {label: 'Quarterly', value: 'Quarterly'},
                                    {label: 'Joining Based', value: 'Joining Based'},
                                  ]}
                                  size='md'
                                  label='Proration Type'
                                  withAsterisk={false}
                                  value={formField?.proRataType}
                                  onChange={(value) => {
                                    setFormField((prev: any) => ({...prev, proRataType: value}))
                                    clearInputError('proRataType', inputError, setInputError)
                                  }}
                                  error={inputError?.proRataType}
                                  placeholder='Select proration type'
                                />
                                <small className='text-muted'>
                                  How leave should be calculated based on time period
                                </small>
                              </div>
                              <div className='col-md-4'>
                                <div className='form-group'>
                                  <label className='form-label'>
                                    Applicable Leave Types
                                  </label>
                                  <MultiSelect
                                    data={leaveType
                                      .filter(lt => lt.label && lt.value)
                                      .map(lt => ({
                                        value: lt.label as string,
                                        label: lt.label as string
                                      }))
                                    }
                                    value={selectedApplicableLeaves}
                                    onChange={(values) => {
                                      setSelectedApplicableLeaves(values)
                                      clearInputError('proRataApplicableLeaves', inputError, setInputError)
                                    }}
                                    placeholder='Select applicable leave types'
                                    searchable
                                    clearable
                                    disabled={leaveTypeLoading}
                                    error={inputError?.proRataApplicableLeaves}
                                    styles={{
                                      input: {
                                        minHeight: '40px',
                                      }
                                    }}
                                  />
                                  {inputError?.proRataApplicableLeaves && (
                                    <div className='text-danger mt-1' style={{fontSize: '0.875rem'}}>
                                      {inputError.proRataApplicableLeaves}
                                    </div>
                                  )}
                                  <small className='text-muted d-block mt-1'>
                                    Select the leave types this pro-rata applies to
                                  </small>
                                </div>
                              </div>
                              <div className='col-md-4'>
                                <CustomTextInput
                                  name='proRataCalculationRules'
                                  label='Calculation Rules'
                                  value={formField?.proRataCalculationRules || ''}
                                  onChange={(event) => {
                                    handleInputChange(event, setFormField, inputError, setInputError)
                                  }}
                                  placeholder='Describe calculation logic'
                                  error={inputError?.proRataCalculationRules}
                                />
                                <small className='text-muted'>
                                  Brief description of how pro-rata is calculated
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )} */}
                  </div>
                </div>
              </div>
            </div>


            {/* Casual Leave Pro-rata Table - Simple and compact */}
            {selectedLeaveTypeName === 'Casual Leave' && (
              <div className='col-lg-4 col-md-12'>
                <div className='card border shadow-sm h-100'>
                  <div className='card-header bg-primary text-white py-2'>
                    <h6 className='mb-0'>CL Pro-rata</h6>
                  </div>
                  <div className='card-body p-2'>
                    <table className='table table-sm table-bordered mb-0'>
                      <thead>
                        <tr>
                          <th className='text-center'>Month</th>
                          <th className='text-center'>Days</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clProRataRows.map((row, index) => (
                          <tr key={index}>
                            <td className='text-center'>
                              {row.month
                                .replace('January', 'Jan')
                                .replace('December', 'Dec')}
                            </td>
                            <td className='text-center'>
                              {row.entitlement === 0 ? 'NIL' : row.entitlement}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Earned Leave Tables - Simple and compact */}
            {selectedLeaveTypeName === 'Earned Leave' && (
              <div className='col-lg-4 col-md-12'>
                <div className='card border shadow-sm h-100'>
                  <div className='card-header bg-light border-0'>
                    {/* <h6 className='mb-0'>EL Tables</h6> */}
                  </div>
                  <div className='card-body p-2'>
                    {/* EL Pro-rata Table */}
                    <div className='mb-3'>
                      <h6 className='text-primary mb-1' style={{ fontSize: '0.8rem' }}>EL Pro-rata</h6>
                      <table className='table table-sm table-bordered mb-0'>
                        <thead>
                          <tr>
                            <th className='text-center' style={{ fontSize: '0.75rem' }}>Month</th>
                            <th className='text-center' style={{ fontSize: '0.75rem' }}>Days</th>
                          </tr>
                        </thead>
                        <tbody>
                          {elProRataRows.slice(0, 6).map((row, index) => (
                            <tr key={index}>
                              <td className='text-center' style={{ fontSize: '0.7rem' }}>
                                {row.month.substring(0, 3)}
                              </td>
                              <td className='text-center' style={{ fontSize: '0.7rem' }}>
                                {row.entitlement === 0 ? 'NIL' : row.entitlement}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* LWP Impact Table */}
                    <div>
                      <h6 className='text-danger mb-1' style={{ fontSize: '0.8rem' }}>LWP Impact</h6>
                      <table className='table table-sm table-bordered mb-0'>
                        <thead>
                          <tr>
                            <th className='text-center' style={{ fontSize: '0.75rem' }}>LWP</th>
                            <th className='text-center' style={{ fontSize: '0.75rem' }}>Cut</th>
                          </tr>
                        </thead>
                        <tbody>
                          {LWP_EL_REDUCTION_TABLE.slice(0, 4).map((row, index) => (
                            <tr key={index}>
                              <td className='text-center' style={{ fontSize: '0.7rem' }}>
                                {row.lwpDays.replace(' days', 'd')}
                              </td>
                              <td className='text-center' style={{ fontSize: '0.7rem' }}>
                                {row.elReduction}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Policy Rules Section - Collapsible */}
          <div className='row g-4 mt-2'>
            <div className='col-12'>
              <div className='card border-0 shadow-sm'>
                <div className='card-header bg-light border-0 d-flex justify-content-between align-items-center'>
                  <h6 className='card-title mb-0 text-dark'>
                    <i className='fas fa-book me-2 text-primary'></i>
                    Leave Policy Reference Guide
                  </h6>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    className='btn-outline-primary'
                    onClick={() => setShowPolicyTable(!showPolicyTable)}
                  >
                    <i className={`fas ${showPolicyTable ? 'fa-eye-slash' : 'fa-eye'} me-1`}></i>
                    {showPolicyTable ? 'Hide' : 'Show'} Policy Rules
                  </Button>
                </div>

                {showPolicyTable && (
                  <div className='card-body p-4'>
                    <div className='table-responsive'>
                      <table className='table table-striped table-hover'>
                        <thead className='table-dark'>
                          <tr>
                            <th>Leave Type</th>
                            <th>Entitlement</th>
                            <th>Max Duration</th>
                            <th>Notice Period</th>
                            <th>Special Conditions</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td><strong className='text-primary'>Short Leave</strong></td>
                            <td>Once per month</td>
                            <td>Up to 2 hours</td>
                            <td>Prior approval required</td>
                            <td><span className='badge bg-warning-subtle text-warning'>Not an entitlement, can be refused</span></td>
                          </tr>
                          <tr>
                            <td><strong className='text-primary'>Casual Leave (CL)</strong></td>
                            <td>8 days per year (Pro-rata)</td>
                            <td>Max 3 days per spell</td>
                            <td>48 hours prior</td>
                            <td><span className='badge bg-info-subtle text-info'>Cannot be clubbed, no carry forward</span></td>
                          </tr>
                          <tr>
                            <td><strong className='text-primary'>Emergency Leave</strong></td>
                            <td>Part of CL quota</td>
                            <td>As per emergency</td>
                            <td>ASAP or 0.5 day before</td>
                            <td><span className='badge bg-danger-subtle text-danger'>Requires supporting proof</span></td>
                          </tr>
                          <tr>
                            <td><strong className='text-primary'>Earned Leave (EL)</strong></td>
                            <td>14 days after 1 year</td>
                            <td>Min 6 consecutive days once/year</td>
                            <td>7 days prior</td>
                            <td><span className='badge bg-success-subtle text-success'>Max 5 days carry forward</span></td>
                          </tr>
                          <tr>
                            <td><strong className='text-primary'>Compensatory Off</strong></td>
                            <td>For holiday/off work</td>
                            <td>1 day per holiday worked</td>
                            <td>Prior approval for holiday work</td>
                            <td><span className='badge bg-warning-subtle text-warning'>Must use within 1 month</span></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {selectedLeaveTypeName === 'Earned Leave' && (
                      <div className='mt-4'>
                        <h6 className='mb-3'>
                          <i className='fas fa-chart-bar me-2 text-primary'></i>
                          Pro-rata Entitlement Table for {selectedLeaveTypeName}
                        </h6>
                        <div className='table-responsive'>
                          <table className='table table-bordered table-hover'>
                            <thead className='table-primary'>
                              <tr>
                                <th>Month of Joining</th>
                                <th className='text-center'>Entitlement (Days)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {elProRataRows.map((row, index) => (
                                <tr key={index}>
                                  <td>{row.month}</td>
                                  <td className='text-center'>
                                    <span className='badge bg-primary'>{row.entitlement}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='modal-footer bg-light border-0'>
          <div className='d-flex justify-content-end align-items-center gap-3 w-100'>
            <Button
              variant='outline'
              size='md'
              leftIcon={<i className='fas fa-times'></i>}
              onClick={() => {
                // Clear all state when canceling
                setSelectedLeaveTypeName('')
                setInputError({})
                cancelSubmit(
                  setFormField,
                  setEditId,
                  setInputError,
                  LeavePolicyInitialInput,
                  setShowForm
                )
              }}
            >
              Cancel
            </Button>
            <Button
              variant='primary'
              size='md'
              loading={submitAPIStatus.loading}
              leftIcon={<i className={`fas ${editId !== null ? 'fa-edit' : 'fa-plus'}`}></i>}
              disabled={!formField?.leaveTypeId || leaveTypeLoading}
              onClick={() => {
                if (leaveTypeLoading) {
                  toast.error('Please wait for leave types to load')
                  return
                }
                if (!formField?.leaveTypeId) {
                  toast.error('Please select a Leave Type before creating a policy')
                  return
                }
                handleSubmit(
                  `${API.LEAVEPOLICY}`,
                  formField,
                  LeavePolicyInitialInput,
                  setLeavePolicy,
                  setEditId,
                  setFormField,
                  editId,
                  setInputError,
                  validateFormWithLeaveType,
                  setSubmitAPIStatus,
                  addLeavePolicies,
                  updateLeavePoliciesById,
                  setShowForm
                )
              }}
            >
              {editId !== null ? 'Update' : 'Create'} Policy
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default LeavePolicy
